import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import { UserWithRole } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface SanctionModalProps {
  user: UserWithRole;
  updating: boolean;
  onSanction: (type: 'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute', duration?: number, reason?: string) => void;
  onCancel: () => void;
  currentUserRoleId?: number;
  context?: 'roles' | 'users'; // Pour vérifier les permissions
}

const SanctionModal: React.FC<SanctionModalProps> = ({
  user,
  updating,
  onSanction,
  onCancel,
  currentUserRoleId,
  context = 'users'
}) => {
  const { t } = usePageTranslation('panel', 'UserRolesManagement');
  const [sanctionType, setSanctionType] = useState<'ban_permanent' | 'ban_temporary' | 'mute' | 'unban' | 'unmute'>('ban_temporary');
  const [duration, setDuration] = useState<number>(1);
  const [durationUnit, setDurationUnit] = useState<'hours' | 'days' | 'weeks' | 'months'>('days');
  const [reason, setReason] = useState('');

  const isCurrentlyBanned = user.bann && user.bann > 0;
  const isCurrentlyMuted = user.muted;

  // Permissions : Seuls Admin (2) et CM (1) peuvent bannir de façon permanente
  const canBanPermanent = currentUserRoleId && currentUserRoleId <= 2;

  // Définir le type par défaut selon les permissions
  useEffect(() => {
    if (!canBanPermanent && sanctionType === 'ban_permanent') {
      setSanctionType('ban_temporary');
    }
  }, [canBanPermanent, sanctionType]);

  const handleConfirm = () => {
    let durationInHours = 0;
    
    if (sanctionType === 'ban_temporary' || sanctionType === 'mute') {
      switch (durationUnit) {
        case 'hours':
          durationInHours = duration;
          break;
        case 'days':
          durationInHours = duration * 24;
          break;
        case 'weeks':
          durationInHours = duration * 24 * 7;
          break;
        case 'months':
          durationInHours = duration * 24 * 30;
          break;
      }
    }

    onSanction(sanctionType, durationInHours > 0 ? durationInHours : undefined, reason || undefined);
  };

  const canApplySanction = () => {
    if (sanctionType === 'unban' && !isCurrentlyBanned) return false;
    if (sanctionType === 'unmute' && !isCurrentlyMuted) return false;
    if ((sanctionType === 'ban_temporary' || sanctionType === 'mute') && (!duration || duration <= 0)) return false;
    return true;
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <Card className="max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold text-white mb-4">
          {t('sanction.title')} - {user.username}
        </h3>

        {/* Statut actuel */}
        <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <h4 className="text-white font-semibold mb-2">{t('sanction.currentStatus')}</h4>
          <div className="space-y-2">
            {isCurrentlyBanned ? (
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/50">
                  {user.ban_end ? t('sanction.bannedTemporary') : t('sanction.bannedPermanent')}
                </span>
                {user.ban_end && (
                  <span className="text-gray-400 text-sm">
                    {t('sanction.until')} {new Date(user.ban_end).toLocaleString('fr-FR')}
                  </span>
                )}
                {user.ban_reason && (
                  <span className="text-gray-400 text-sm">- {user.ban_reason}</span>
                )}
              </div>
            ) : (
              <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
                {t('sanction.notBanned')}
              </span>
            )}
            {isCurrentlyMuted ? (
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/50">
                  {t('sanction.muted')}
                </span>
                {user.mute_end && (
                  <span className="text-gray-400 text-sm">
                    {t('sanction.until')} {new Date(user.mute_end).toLocaleString('fr-FR')}
                  </span>
                )}
                {user.mute_reason && (
                  <span className="text-gray-400 text-sm">- {user.mute_reason}</span>
                )}
              </div>
            ) : (
              <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
                {t('sanction.notMuted')}
              </span>
            )}
          </div>
        </div>

        {/* Type de sanction */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            {t('sanction.selectType')}
          </label>
          <div className="grid grid-cols-2 gap-3">
            {!isCurrentlyBanned && (
              <>
                {canBanPermanent && (
                  <button
                    onClick={() => setSanctionType('ban_permanent')}
                    className={`p-3 rounded-lg border transition-colors text-left ${
                      sanctionType === 'ban_permanent'
                        ? 'bg-red-500/20 text-red-400 border-red-500/50'
                        : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-700/50'
                    }`}
                  >
                      <div className="font-semibold">{t('sanction.banPermanent')}</div>
                    <div className="text-xs text-gray-400 mt-1">{t('sanction.banPermanentDesc')}</div>
                  </button>
                )}
                <button
                  onClick={() => setSanctionType('ban_temporary')}
                  className={`p-3 rounded-lg border transition-colors text-left ${
                    sanctionType === 'ban_temporary'
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/50'
                      : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-700/50'
                  }`}
                >
                  <div className="font-semibold">{t('sanction.banTemporary')}</div>
                  <div className="text-xs text-gray-400 mt-1">{t('sanction.banTemporaryDesc')}</div>
                </button>
              </>
            )}
            {isCurrentlyBanned && (
              <button
                onClick={() => setSanctionType('unban')}
                className={`p-3 rounded-lg border transition-colors text-left ${
                  sanctionType === 'unban'
                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                    : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-700/50'
                }`}
              >
                <div className="font-semibold">{t('sanction.unban')}</div>
                <div className="text-xs text-gray-400 mt-1">{t('sanction.unbanDesc')}</div>
              </button>
            )}
            {!isCurrentlyMuted ? (
              <button
                onClick={() => setSanctionType('mute')}
                className={`p-3 rounded-lg border transition-colors text-left ${
                  sanctionType === 'mute'
                    ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                    : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-700/50'
                }`}
              >
                <div className="font-semibold">{t('sanction.mute')}</div>
                <div className="text-xs text-gray-400 mt-1">{t('sanction.muteDesc')}</div>
              </button>
            ) : (
              <button
                onClick={() => setSanctionType('unmute')}
                className={`p-3 rounded-lg border transition-colors text-left ${
                  sanctionType === 'unmute'
                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                    : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-700/50'
                }`}
              >
                <div className="font-semibold">{t('sanction.unmute')}</div>
                <div className="text-xs text-gray-400 mt-1">{t('sanction.unmuteDesc')}</div>
              </button>
            )}
          </div>
        </div>

        {/* Durée pour ban temporaire et mute */}
        {(sanctionType === 'ban_temporary' || sanctionType === 'mute') && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('sanction.duration')}
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value) || 1)}
                className="w-20 px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <select
                value={durationUnit}
                onChange={(e) => setDurationUnit(e.target.value as 'hours' | 'days' | 'weeks' | 'months')}
                className="px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="hours">{t('sanction.hours')}</option>
                <option value="days">{t('sanction.days')}</option>
                <option value="weeks">{t('sanction.weeks')}</option>
                <option value="months">{t('sanction.months')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Raison */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {t('sanction.reason')} ({t('modal.changeRole.optional')})
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('sanction.reasonPlaceholder')}
            rows={3}
            className="w-full px-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Boutons */}
        <div className="flex space-x-3">
          <button
            onClick={handleConfirm}
            disabled={updating || !canApplySanction()}
            className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updating ? t('sanction.applying') : t('sanction.confirm')}
          </button>
          <button
            onClick={onCancel}
            disabled={updating}
            className="flex-1 px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            {t('modal.changeRole.cancel')}
          </button>
        </div>
      </Card>
    </div>,
    document.body
  );
};

export default SanctionModal;

